<template>
  <nav
    :class="[
      'tab-bar__wrapper',
      stickyTabBar ? '' : 'is-not-sticky',
      { 'is-over-menu': isMenuOpen }
    ]"
  >
    <router-link
      :to="$routes.SWAP_LIST"
      :class="['tab-bar__item', { 'is-active': activeNavItem === 'swapList' }]"
      active-class=""
      exact-active-class=""
      @click.native="itemClick(activeNavItem === 'swapList')"
    >
      <BaseIcon
        :icon-file="
          activeNavItem === 'swapList'
            ? 'icons/tabbar/swaps-active'
            : 'icons/tabbar/swaps'
        "
        class="tab-bar__item__icon"
      />
      {{ $t('tab_bar_swap_suggestions') }}
    </router-link>

    <router-link
      :to="$routes.MATCHES"
      :class="['tab-bar__item', { 'is-active': activeNavItem === 'matches' }]"
      active-class=""
      exact-active-class=""
      @click.native="itemClick(activeNavItem === 'matches')"
    >
      <FullSwapNotification
        v-if="showFullSwapNotification"
        @onAnimationEnd="handleNotificationAnimEnd"
      />
      <BaseIcon
        :icon-file="
          activeNavItem === 'matches'
            ? 'icons/tabbar/matches-active'
            : 'icons/tabbar/matches'
        "
        class="tab-bar__item__icon"
      />
      {{ $t('tab_bar_matches') }}
      <!-- Matchningsvy v3: something to see there is a still dot, not a
           count. -->
      <NotificationDot
        v-if="isMatchesV3 && unreadMatchesAndRequests > 0"
        still
        class="notification-dot matches-notification-dot"
      />
      <BadgeCount
        v-if="!isMatchesV3"
        :notifications="notifications && notifications.unreadMatchesAndRequests"
        class="absolute"
      />
    </router-link>

    <router-link
      v-if="!primaryPropositionIsPremiumOrFree"
      :to="$routes.PAYMENT_PROPOSITION + '/' + primaryPropositionId"
      class="tab-bar__item premium"
    >
      <template slot-scope="{ route, isActive }">
        <router-link :to="route" @click.native="itemClick(isActive)">
          <BaseIcon
            :icon-file="
              isActive ? 'icons/tabbar/premium-active' : 'icons/tabbar/premium'
            "
            :width="38"
            :height="38"
            class="tab-bar__item__icon"
          />
          Premium
        </router-link>
      </template>
    </router-link>

    <button
      v-if="showBoostButton"
      aria-label="Boost"
      class="tab-bar__item premium"
      @click="handleBoostClick"
    >
      <BoostEndedIcon
        v-if="showBoostEndedIcon"
        :size="44"
        class="tab-bar__item__icon"
      />
      <BaseIcon
        v-else
        :icon-file="
          hasBoost ? 'icons/tabbar/boost-has-boost' : 'icons/tabbar/boost'
        "
        :width="38"
        :height="38"
        :class="['tab-bar__item__icon', { 'has-boost': hasBoost }]"
      />
      Boost
    </button>

    <router-link
      :to="$routes.MY_PROPOSITIONS"
      :class="['tab-bar__item', { 'is-active': activeNavItem === 'myProp' }]"
      active-class=""
      exact-active-class=""
      @click.native="itemClick(activeNavItem === 'myProp')"
    >
      <BaseIcon
        :icon-file="
          activeNavItem === 'myProp'
            ? 'icons/tabbar/my-prop-active'
            : 'icons/tabbar/my-prop'
        "
        class="tab-bar__item__icon"
      />
      {{ $t('tab_bar_my_ads') }}
    </router-link>

    <button
      :class="['tab-bar__item', { 'is-active': isMenuOpen }]"
      :aria-label="$t('tab_bar_more')"
      @click="toggleMenu"
    >
      <BaseIcon
        :icon-file="
          isMenuOpen ? 'icons/tabbar/menu-active' : 'icons/tabbar/menu'
        "
        class="tab-bar__item__icon"
      />
      {{ $t('tab_bar_more') }}
    </button>
  </nav>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import BadgeCount from './Icons/BadgeCount';
import BoostEndedIcon from './BoostEndedIcon';
import events from '@/utils/helpers/events';
import NotificationDot from './Icons/NotificationDot';
import FullSwapNotification from '@/components/FullSwap/FullSwapNotification';
import { getActiveNavItem } from '@/utils/activeNavItem';
import { isMatchesV3Enabled } from '@/utils/matchesV3';
import BaseIcon from '@/components/BaseIcon.vue';

export default {
  name: 'TabBar',
  components: {
    BadgeCount,
    BoostEndedIcon,
    NotificationDot,
    FullSwapNotification,
    BaseIcon
  },

  data: () => ({
    showFullSwapNotification: false
  }),

  computed: {
    isMatchesV3() {
      return isMatchesV3Enabled(this.$growthbook);
    },

    unreadMatchesAndRequests() {
      return (
        (this.notifications && this.notifications.unreadMatchesAndRequests) || 0
      );
    },

    ...mapGetters({
      isTestEnabled: 'app/isTestEnabled',
      inReboostWindow: 'extraVisibility/inReboostWindow',
      notifications: 'menu/getNotifications',
      user: 'app/user',
      stickyTabBar: 'menu/stickyTabBar',
      isMenuOpen: 'menu/isMenuOpen',
      swapInterestCountsUnseenTotal: 'swapList/swapInterestCountsUnseenTotal',
      showSwapAddedBadge: 'swapList/showSwapAddedBadge',
      primaryProposition: 'myPropositions/primaryProposition',
      primaryPropositionId: 'myPropositions/primaryPropositionId',
      primaryPropositionIsPremiumOrFree:
        'myPropositions/primaryPropositionIsPremiumOrFree',
      newMatch: 'matches/newMatch',
      primaryPropositionActive: 'myPropositions/primaryPropositionActive'
    }),

    hasBoost() {
      return this.primaryProposition.extraVisibility;
    },

    consumablesEnabled() {
      return this.isTestEnabled('consumables');
    },

    showBoostButton() {
      return (
        this.primaryPropositionActive &&
        this.primaryPropositionIsPremiumOrFree &&
        this.$country.isFeatureEnabled('PAYMENT')
      );
    },

    // Consumables A/B: the ad whose re-boost window the button reads —
    // none for a hidden button, and the flag is read last, so nobody else
    // is evaluated or makes the request. Read even while the ad carries a
    // boost: its extraVisibility (and endDate) stay for a while after the
    // boost ended, so only /api/boost-summary knows it has ended.
    reboostWindowPropositionId() {
      if (!this.showBoostButton) return null;
      if (!this.consumablesEnabled) return null;
      return this.primaryProposition.propositionId || null;
    },

    // For 24 h after the boost ended the button pulses and (see
    // handleBoostClick) reopens the result panel.
    showBoostEndedIcon() {
      return this.consumablesEnabled && this.inReboostWindow;
    },

    activeNavItem() {
      return getActiveNavItem({
        path: this.$route.path,
        query: this.$route.query
      });
    }
  },

  watch: {
    reboostWindowPropositionId: {
      immediate: true,
      handler(propositionId) {
        if (propositionId) this.loadReboostWindow(propositionId);
      }
    },

    // A boost bought (or dropped) on the ad: the window read before it
    // is stale, so read it again.
    hasBoost(now, before) {
      if (!now === !before || !this.reboostWindowPropositionId) return;
      this.loadReboostWindow(this.reboostWindowPropositionId);
    },

    newMatch(event) {
      // Show notification only if the event was triggered by the other user
      if (
        event &&
        event.triggeredByUser !== undefined &&
        event.triggeredByUser === false
      ) {
        this.showFullSwapNotification = true;
      }
    }
  },

  methods: {
    ...mapActions({
      toggleMenu: 'menu/toggleMenu',
      setGlobalModal: 'ui/setGlobalModal',
      loadReboostWindow: 'extraVisibility/loadReboostWindow'
    }),

    ...mapMutations({
      setExtraVisibilityPaymentVisible: 'extraVisibility/setVisible',
      setExtraVisibilityPaymentProposition: 'extraVisibility/setProposition'
    }),

    handleNotificationAnimEnd() {
      this.showFullSwapNotification = false;
    },

    async handleBoostClick() {
      // Consumables A/B: for 24 h after the boost ended the button reopens
      // the result panel with the re-boost offer; control goes to the
      // purchase as before. A fresh read, so the click and the icon agree,
      // and it comes first: the ad can still carry an ended boost.
      const inReboostWindow =
        this.consumablesEnabled &&
        (await this.loadReboostWindow(this.primaryProposition.propositionId));
      if (inReboostWindow || this.hasBoost) {
        this.setGlobalModal('BoostInfo');
        events.emitEvent(events.eventTypes.CLICK, 'Boost Statistics', {
          src: 'boost-info-tabbar'
        });
        return;
      }
      this.setExtraVisibilityPaymentProposition(this.primaryProposition);
      this.setExtraVisibilityPaymentVisible(true);
    },

    itemClick(isActive) {
      if (isActive) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.tab-bar__wrapper {
  position: fixed;
  bottom: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: calc(#{$tab-bar-height} + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  background-color: #fff;
  border-top: 1px solid #f1f2f6;
  box-shadow: 0px 3px 5px rgba(176, 190, 197, 0.25),
    0px 8px 24px rgba(176, 190, 197, 0.4), 0px 0px 15px rgba(0, 0, 0, 0.1);
  z-index: 199;

  // Remove position fixed when keyboard is open
  // to prevent that too much screen space is covered
  @media (max-height: 500px) {
    position: absolute;
  }

  // Keep position fixed in landscape mode
  @media ($mobile) and (orientation: landscape) {
    position: fixed;
  }

  &.is-not-sticky {
    position: absolute;
  }

  // While the menu is open the tab bar stays visible on top of it and its
  // dark backdrop, like the app. The menu ends above it (MobileMenu).
  &.is-over-menu {
    position: fixed;
    z-index: $mobile-menu-z-index + 1;
  }
}

.tab-bar__item {
  background: none;
  border: none;
  padding: 6px 0;
  margin: 0;
  user-select: none;
  align-items: center;
  color: var(--text-secondary, #787878);
  display: flex;
  flex-direction: column;
  flex: 1;
  font-size: 11px;
  font-weight: 600;
  gap: 4px;
  height: $tab-bar-height;
  justify-content: center;
  position: relative;
  text-decoration: none;
  white-space: nowrap;

  &.router-link-active,
  &.is-active {
    color: var(--text-link, #2276fc);
    font-weight: 700;
  }

  a {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 4px;
    color: var(--text-secondary, #787878);

    &.router-link-active,
    &.is-active {
      color: var(--text-link, #2276fc);
      font-weight: 700;
    }
  }

  &.premium {
    margin-bottom: 11px;
    gap: 2px;
    padding-top: 10px;
  }
}

.tab-bar__item__icon {
  width: 26px;
  height: 26px;

  .premium & {
    width: 38px;
    height: 38px;
  }
}

.notification-dot {
  top: 6px;
  right: 16px;
}

.swap-added-icon {
  visibility: hidden;
  position: absolute;
  top: 6px;
  right: 14px;
  width: 12px;
  height: 12px;
  background-color: #2ec973;
  border-radius: 50%;
  font-size: 8px;
  color: #2ec973;
  display: grid;
  place-items: center;
}

.plus-one-enter-active {
  visibility: visible;
  animation-name: plus-one;
  animation-duration: 1.5s;
  animation-timing-function: ease-in-out;
}

@keyframes plus-one {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(4);
    border: none;
    color: #fff;
    padding-right: 1px;
  }
  100% {
    transform: scale(1);
  }
}
</style>
